import { DateTime } from 'luxon';
import { IJob, Job, JobStatus } from '.';
import { Gender, Languages, PDSRecord } from '../types';
import { formatPdsRecord, generatePdsWarnings } from './util';

export enum DispositionType {
    ADMIN = 'admin',
    FOLLOW_UP = 'followUp',
}

const initialValues = {
    activityType: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    gender: '',
    firstName: '',
    middleName: '',
    lastName: '',
    postCode: '',
    town: '',
    nhsNumber: '',
    contactNumber: '',
    languagesSpoken: [],
    dateOfBirth: '',
    additionalContactNumbers: '',
    availableFrom: '',
    availableTo: '',
    referredBy: '',
    referrer: '',
    referralDateTime: '',
    referralPathway: '',
    disposition: '',
    careComplexity: '',
    carRequired: false,
    staffPreferredGender: [],
};

export const friendlyPatientStatuses = {
    PENDING: 'Unassigned',
    AVAILABLE: 'Assigned',
    ACCEPTED: 'Accepted',
    CURRENT: 'Current',
    ARRIVED: 'Arrived',
    COMPLETED: 'Completed',
    HCP_ABORTED: 'Aborted by HCP',
    WITHDRAWN: 'Withdrawn',
    CONTROLLER_ABORTED: 'Archived',
    REMOVED: 'Removed',
};

export const friendlyVisitStatuses = {
    PENDING: 'Unassigned',
    AVAILABLE: 'Assigned',
    ACCEPTED: 'Not started',
    CURRENT: 'Current',
    ARRIVED: 'Arrived',
    COMPLETED: 'Completed',
    HCP_ABORTED: 'Aborted by HCP',
    WITHDRAWN: 'Withdrawn',
    CONTROLLER_ABORTED: 'Aborted by COD',
};

export interface IAdmission {
    isAdmitted: boolean;
    admittedBy?: string;
    admittedAt?: string;
    dischargedBy?: string;
    dischargedAt?: string;
}

export interface PatientAddress {
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    town?: string;
    postCode?: string;
}

export interface PatientName {
    firstName: string;
    lastName: string;
    middleName?: string;
}

export interface FormattedPDSPeriod {
    period?: {
        start: string;
        end?: string;
    };
}

export interface FormattedPDSName extends FormattedPDSPeriod {
    firstName?: string;
    middleName?: string;
    lastName: string;
    use: string;
}

export interface FormattedPDSAddress extends FormattedPDSPeriod {
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    postCode?: string;
    town?: string;
    county?: string;
    use: string;
}

export interface FormattedPDSContact extends FormattedPDSPeriod {
    value: string;
    use: string;
}

export type FormattedPDSRecord = {
    versionId: string;
    nhsNumber: string;
    dateOfBirth?: string;
    gender: Gender;
    name: FormattedPDSName[];
    address: FormattedPDSAddress[];
    contactNumber: FormattedPDSContact[];
    deceasedDateTime?: string;
    singleDataSet: {
        name: boolean;
        address: boolean;
        contactNumber: boolean;
    };
    security: {
        code: string;
        display: string;
    };
};

export interface PDSWarning {
    field: string;
    id?: string;
    data: {
        currentValue: string | string[] | Record<string, unknown>;
        pdsValue: string | FormattedPDSName[] | FormattedPDSAddress[] | FormattedPDSContact[];
    };
}

export interface PDSData {
    versionId: string;
    raw?: PDSRecord;
    record?: FormattedPDSRecord;
    warnings?: PDSWarning[];
}

export interface IPatient extends IJob {
    dateOfBirth?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    nhsNumber: string;
    gender?: Gender;
    activityType?: string;
    additionalContactNumbers?: string[];
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    availableFrom?: string;
    availableTo?: string;
    contactNumber?: string;
    languagesSpoken?: Languages[];
    disposition?: DispositionType | string;
    postCode?: string;
    town?: string;
    county?: string;
    referredBy?: string;
    referrer?: string;
    referralDateTime?: string;
    referralPathway?: string;
    careComplexity?: string | null;
    carRequired?: boolean;
    dateOfVisit?: string;
    earliestDateOfVisit?: string;
    initialVisit?: string;
    lastUpdatedDateTime?: string;
    staffPreferredGender?: string[];
    pds?: PDSData;
    admission?: IAdmission;
}

interface Patient extends IPatient {}

class Patient extends Job {
    static INITIAL_VALUES = {
        ...Job.INITIAL_VALUES,
        ...initialValues,
    };

    constructor(data: IPatient) {
        super(data);
        Object.assign(this, data);
        if (data.pds?.raw) {
            this.setPdsData(data.pds.raw);
        }
    }

    static getFriendlyPatientStatus(jobStatus: JobStatus): string {
        return friendlyPatientStatuses[jobStatus];
    }

    get friendlyPatientStatus(): string {
        return friendlyPatientStatuses[this.jobStatus];
    }

    static getFriendlyVisitStatus(jobStatus: JobStatus): string {
        return friendlyVisitStatuses[jobStatus];
    }

    get friendlyVisitStatus(): string {
        return friendlyVisitStatuses[this.jobStatus];
    }

    get age(): number | null {
        if (!this.dateOfBirth) {
            return null;
        }

        const currentDate = DateTime.now();
        const dateOfBirth = DateTime.fromISO(this.dateOfBirth);
        return Math.floor(currentDate.diff(dateOfBirth, 'years').years);
    }

    setPdsData(rawRecord: PDSRecord): void {
        this.pds = {
            ...(this.pds || { versionId: '-1' }),
            raw: rawRecord,
            record: formatPdsRecord(rawRecord),
        };

        if (this.pds.versionId !== rawRecord.meta?.versionId) {
            this.pds.warnings = generatePdsWarnings(this);
        }
    }

    generatePdsWarnings(): void {
        if (this.pds?.record) {
            this.pds.warnings = generatePdsWarnings(this);
        }
    }
}

enum SortOrder {
    ASC = 'ASC',
    DESC = 'DESC',
}

interface PatientListFilters {
    nameOrNhsNumber?: string[];
    showDischargedPatients?: boolean;
}

interface PatientListSort {
    sortByName?: SortOrder;
    sortByNHSNumber?: SortOrder;
    sortByDob?: SortOrder;
    sortByGender?: SortOrder;
    sortByPostcode?: SortOrder;
    sortByLastCompletedJob?: SortOrder;
    sortByNextUpcomingJob?: SortOrder;
    sortByAdmissions?: SortOrder;
}

export { Gender, Patient, PatientListFilters, PatientListSort, SortOrder };
